/**
 * Scanner - Package.json dependency scanner for Brick modules
 * Discovers Brick modules in node_modules and validates their structure
 */

import fs from "fs-extra";
import { glob } from "glob";
import path from "path";
import pc from "picocolors";
import type { ComponentConfig } from "./main-index";

export interface ScannedModule {
  name: string;
  path: string;
  specPath: string;
  packageJsonPath: string;
  version: string;
  hasIosImplementation: boolean;
  hasAndroidImplementation: boolean;
  brickConfig?: any;
  // Android-specific configuration from package.json brickModule
  androidPackage?: string;
  androidModuleName?: string;
  androidPath?: string;
}

export interface ScanResult {
  modules: ScannedModule[];
  errors: string[];
  warnings: string[];
}

export class Scanner {
  private config: ComponentConfig;

  constructor(config: ComponentConfig) {
    this.config = config;
  }

  /**
   * Scans package.json dependencies for Brick modules
   * In library mode: only scans current package for .brick.ts files
   * In user project mode: scans dependencies for Brick modules
   */
  async scanBrickModules(): Promise<ScannedModule[]> {
    const result: ScannedModule[] = [];

    try {
      const packageJsonPath = path.join(
        this.config.projectRoot,
        "package.json",
      );

      if (!(await fs.pathExists(packageJsonPath))) {
        throw new Error(`package.json not found at ${packageJsonPath}`);
      }

      const packageJson = await fs.readJson(packageJsonPath);

      // Check if this is library mode from config (set by main-index.ts based on project type)
      const isLibraryMode = this.config.libraryMode;

      if (isLibraryMode) {
        // Library mode: scan current package for .brick.ts files
        const libraryModules = await this.scanCurrentLibrary();
        result.push(...libraryModules);
      } else {
        // User project mode: scan dependencies
        const allDeps = {
          ...(packageJson.dependencies || {}),
          ...(packageJson.devDependencies || {}),
          ...(packageJson.peerDependencies || {}),
        };

        this.log(
          `🔍 Scanning ${
            Object.keys(allDeps).length
          } dependencies for Brick modules...`,
        );

        // Scan each dependency
        for (const [depName, version] of Object.entries(allDeps)) {
          try {
            const scannedModule = await this.scanSingleModule(
              depName,
              version as string,
            );
            if (scannedModule) {
              result.push(scannedModule);
              this.log(`✅ Found Brick module: ${depName}`);
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            this.log(`⚠️  Error scanning module ${depName}: ${errorMessage}`);
          }
        }

        // Also scan local modules (for development)
        const localModules = await this.scanLocalModules();
        result.push(...localModules);
      }

      this.log(`📦 Found ${result.length} total Brick modules`);

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to scan Brick modules: ${errorMessage}`);
    }
  }

  /**
   * Scans a single module to determine if it's a Brick module
   */
  private async scanSingleModule(
    depName: string,
    version: string,
  ): Promise<ScannedModule | null> {
    try {
      // Use require.resolve to find the package
      const packageJsonPath = require.resolve(`${depName}/package.json`, {
        paths: [this.config.projectRoot],
      });
      const modulePath = path.dirname(packageJsonPath);

      const packageJson = await fs.readJson(packageJsonPath);

      // Check if it's a Brick module
      const isBrickModule = this.isBrickModule(packageJson);
      if (!isBrickModule) {
        return null;
      }

      // Look for spec file using brickModule.specSrc
      const specPath = await this.findSpecFile(modulePath, packageJson);
      if (!specPath) {
        this.log(`⚠️  Brick module ${depName} missing *.brick.ts file`);
        return null;
      }

      // Check for platform implementations
      const hasIosImplementation = await this.hasIosImplementation(modulePath);
      const hasAndroidImplementation =
        await this.hasAndroidImplementation(modulePath);

      return {
        name: depName,
        path: modulePath,
        specPath,
        packageJsonPath,
        version: packageJson.version || version,
        hasIosImplementation,
        hasAndroidImplementation,
        brickConfig: packageJson.brickModule,
        // Android-specific configuration from package.json brickModule
        androidPackage: packageJson.brickModule?.androidPackage,
        androidModuleName: packageJson.brickModule?.androidModuleName,
        androidPath: hasAndroidImplementation
          ? path.join(modulePath, "android")
          : undefined,
      };
    } catch (error) {
      // Module not found or not resolvable
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.log(`⚠️  Could not resolve module ${depName}: ${errorMessage}`);
      return null;
    }
  }

  /**
   * Checks if a package.json indicates a Brick module
   */
  private isBrickModule(packageJson: any): boolean {
    // Primary check: brickModule field exists
    if (packageJson.brickModule) {
      return true;
    }

    if (
      packageJson.dependencies?.["brick-module"] ||
      packageJson.peerDependencies?.["brick-module"]
    ) {
      return true;
    }

    return false;
  }

  /**
   * Finds the spec file for a module
   */
  private async findSpecFile(
    modulePath: string,
    packageJson?: any,
  ): Promise<string | null> {
    // First, check if brickModule.specSrc is specified
    if (packageJson?.brickModule?.specSrc) {
      const specSrc = packageJson.brickModule.specSrc;
      const specDirPath = path.join(modulePath, specSrc);

      // Check if specSrc is a directory or file
      try {
        const stat = await fs.stat(specDirPath);

        if (stat.isDirectory()) {
          // It's a directory, search for *.brick.ts files in it
          const files = await glob("*.brick.ts", {
            cwd: specDirPath,
          });

          if (files.length > 0 && files[0]) {
            return path.join(specDirPath, files[0]);
          }
        } else if (stat.isFile() && specSrc.endsWith(".brick.ts")) {
          // It's a direct file path
          return specDirPath;
        }
      } catch {
        // Path doesn't exist or other error, continue with fallbacks
      }
    }

    return null;
  }

  /**
   * Checks if module has iOS implementation
   */
  private async hasIosImplementation(modulePath: string): Promise<boolean> {
    const iosPath = path.join(modulePath, "ios");

    if (!(await fs.pathExists(iosPath))) {
      return false;
    }

    // Look for Swift files
    try {
      const swiftFiles = await glob("**/*.swift", { cwd: iosPath });
      return swiftFiles.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * Checks if module has Android implementation
   */
  private async hasAndroidImplementation(modulePath: string): Promise<boolean> {
    const androidPath = path.join(modulePath, "android");

    if (!(await fs.pathExists(androidPath))) {
      return false;
    }

    // Look for Java/Kotlin files
    try {
      const javaFiles = await glob("**/*.{java,kt}", { cwd: androidPath });
      return javaFiles.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * Scans for local Brick modules (for development)
   */
  private async scanLocalModules(): Promise<ScannedModule[]> {
    const result: ScannedModule[] = [];

    // Look for spec files in current project
    try {
      // Get the TypeScript output directory to ignore it during scanning
      const tsOutputDir = this.config.outputTypescript || ".brick";
      const ignorePatterns = [
        "node_modules/**",
        `${tsOutputDir}/**`,
        "build/**",
        "lib/**",
      ];

      const specFiles = await glob("**/*.brick.ts", {
        cwd: this.config.projectRoot,
        ignore: ignorePatterns,
      });

      for (const specFile of specFiles) {
        const specPath = path.join(this.config.projectRoot, specFile);
        const modulePath = path.dirname(specPath);
        const packageJsonPath = path.join(modulePath, "package.json");

        let packageJson: any = {};
        if (await fs.pathExists(packageJsonPath)) {
          packageJson = await fs.readJson(packageJsonPath);
        }

        const moduleName = packageJson.name || path.basename(modulePath);

        const hasAndroidImplementation =
          await this.hasAndroidImplementation(modulePath);

        result.push({
          name: moduleName,
          path: modulePath,
          specPath,
          packageJsonPath,
          version: packageJson.version,
          hasIosImplementation: await this.hasIosImplementation(modulePath),
          hasAndroidImplementation,
          brickConfig: packageJson.brickModule,
          // Android-specific configuration from package.json brickModule
          androidPackage:
            packageJson.brickModule?.android?.package ||
            packageJson.brickModule?.androidPackage,
          androidModuleName:
            packageJson.brickModule?.android?.moduleName ||
            packageJson.brickModule?.androidModuleName,
          androidPath: hasAndroidImplementation
            ? path.join(modulePath, "android")
            : undefined,
        });

        this.log(`🏠 Found local Brick module: ${moduleName}`);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.log(`⚠️  Error scanning local modules: ${errorMessage}`);
    }

    return result;
  }

  /**
   * Validates implementations for a module
   */
  async validateImplementations(module: ScannedModule): Promise<string[]> {
    const errors: string[] = [];

    // Check spec file exists and is readable
    try {
      const specContent = await fs.readFile(module.specPath, "utf8");
      if (specContent.trim().length === 0) {
        errors.push(`Spec file is empty: ${module.specPath}`);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      errors.push(
        `Cannot read spec file: ${module.specPath} - ${errorMessage}`,
      );
    }

    // Check for platform implementations
    if (!module.hasIosImplementation && !module.hasAndroidImplementation) {
      errors.push(`Module ${module.name} has no platform implementations`);
    }

    // Validate iOS implementation if it exists
    if (module.hasIosImplementation) {
      const iosErrors = await this.validateIosImplementation(module);
      errors.push(...iosErrors);
    }

    // Validate Android implementation if it exists
    if (module.hasAndroidImplementation) {
      const androidErrors = await this.validateAndroidImplementation(module);
      errors.push(...androidErrors);
    }

    return errors;
  }

  /**
   * Validates iOS implementation
   */
  private async validateIosImplementation(
    module: ScannedModule,
  ): Promise<string[]> {
    const errors: string[] = [];
    const iosPath = path.join(module.path, "ios");

    try {
      // Look for main module Swift file
      const swiftFiles = await glob("**/*.swift", { cwd: iosPath });

      if (swiftFiles.length === 0) {
        errors.push(`No Swift files found in ${module.name}/ios`);
        return errors;
      }

      // Check for BrickCoreModule inheritance
      let foundBrickModule = false;

      for (const swiftFile of swiftFiles) {
        const filePath = path.join(iosPath, swiftFile);
        const content = await fs.readFile(filePath, "utf8");

        if (content.includes("BrickCoreModule")) {
          foundBrickModule = true;
          break;
        }
      }

      if (!foundBrickModule) {
        errors.push(
          `No BrickCoreModule inheritance found in ${module.name} iOS implementation`,
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      errors.push(
        `Error validating iOS implementation for ${module.name}: ${errorMessage}`,
      );
    }

    return errors;
  }

  /**
   * Validates Android implementation
   */
  private async validateAndroidImplementation(
    module: ScannedModule,
  ): Promise<string[]> {
    const errors: string[] = [];
    const androidPath = path.join(module.path, "android");

    try {
      // Look for Java/Kotlin files
      const javaFiles = await glob("**/*.{java,kt}", { cwd: androidPath });

      if (javaFiles.length === 0) {
        errors.push(`No Java/Kotlin files found in ${module.name}/android`);
      }

      // TODO: Add more Android-specific validation when Android support is implemented
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      errors.push(
        `Error validating Android implementation for ${module.name}: ${errorMessage}`,
      );
    }

    return errors;
  }

  /**
   * Gets detailed scan results with validation
   */
  async getDetailedScanResults(): Promise<ScanResult> {
    const result: ScanResult = {
      modules: [],
      errors: [],
      warnings: [],
    };

    try {
      result.modules = await this.scanBrickModules();

      // Validate each module
      for (const module of result.modules) {
        const validationErrors = await this.validateImplementations(module);

        if (validationErrors.length > 0) {
          result.errors.push(
            ...validationErrors.map((err) => `${module.name}: ${err}`),
          );
        }

        // Add warnings for missing implementations
        if (!module.hasIosImplementation) {
          result.warnings.push(`${module.name}: Missing iOS implementation`);
        }

        if (!module.hasAndroidImplementation) {
          result.warnings.push(
            `${module.name}: Missing Android implementation`,
          );
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      result.errors.push(`Scan failed: ${errorMessage}`);
    }

    return result;
  }

  /**
   * Scans current library for .brick.ts files (library mode only)
   */
  async scanCurrentLibrary(): Promise<ScannedModule[]> {
    const result: ScannedModule[] = [];

    try {
      this.log(
        `🏗️ Library mode: scanning current package for .brick.ts files...`,
      );

      const packageJsonPath = path.join(
        this.config.projectRoot,
        "package.json",
      );
      if (!(await fs.pathExists(packageJsonPath))) {
        throw new Error(
          `package.json not found in current library: ${packageJsonPath}`,
        );
      }

      const packageJson = await fs.readJson(packageJsonPath);
      const moduleName = packageJson.name;

      // Check if this is a Brick module
      if (!this.isBrickModule(packageJson)) {
        this.log(
          `⚠️  Current package is not a Brick module: missing 'brickModule' field`,
        );
        return result;
      }

      // Find .brick.ts files in current package
      const specFiles = await glob("src/**/*.brick.ts", {
        cwd: this.config.projectRoot,
        absolute: false,
      });

      if (specFiles.length === 0) {
        this.log(`⚠️  No .brick.ts files found in current library`);
        return result;
      }

      for (const specFile of specFiles) {
        const specPath = path.join(this.config.projectRoot, specFile);
        const hasAndroidImplementation = await this.hasAndroidImplementation(
          this.config.projectRoot,
        );

        result.push({
          name: moduleName,
          path: this.config.projectRoot,
          specPath,
          packageJsonPath,
          version: packageJson.version,
          hasIosImplementation: await this.hasIosImplementation(
            this.config.projectRoot,
          ),
          hasAndroidImplementation,
          brickConfig: packageJson.brickModule,
          // Android-specific configuration from package.json brickModule
          androidPackage:
            packageJson.brickModule?.android?.package ||
            packageJson.brickModule?.androidPackage,
          androidModuleName:
            packageJson.brickModule?.android?.moduleName ||
            packageJson.brickModule?.androidModuleName,
          androidPath: hasAndroidImplementation
            ? path.join(this.config.projectRoot, "android")
            : undefined,
        });

        this.log(
          `📦 Found current library module: ${moduleName} (${specFile})`,
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to scan current library: ${errorMessage}`);
    }

    return result;
  }

  private log(...args: any[]): void {
    if (this.config.debug) {
      console.log(pc.gray("[scanner]"), ...args);
    }
  }
}
